import { Link } from 'react-router-dom';

const IOS_URL = import.meta.env.VITE_IOS_STORE_URL as string | undefined;
const ANDROID_URL = import.meta.env.VITE_ANDROID_STORE_URL as string | undefined;

interface AppStoreBadgesProps {
  className?: string;
  layout?: 'row' | 'column';
  showComingSoonHint?: boolean;
}

function badgeHref(platform: 'ios' | 'android'): string {
  const url = platform === 'ios' ? IOS_URL : ANDROID_URL;
  if (url?.trim()) return url.trim();
  return '/contact?app=waitlist';
}

function isLive(platform: 'ios' | 'android'): boolean {
  const url = platform === 'ios' ? IOS_URL : ANDROID_URL;
  return Boolean(url?.trim());
}

export function AppStoreBadges({
  className = '',
  layout = 'row',
  showComingSoonHint = true,
}: AppStoreBadgesProps) {
  const iosLive = isLive('ios');
  const androidLive = isLive('android');
  const comingSoon = !iosLive && !androidLive;

  return (
    <div className={className}>
      {showComingSoonHint && comingSoon ? (
        <p className="app-badge-hint">Mobile app — coming soon to App Store &amp; Google Play</p>
      ) : null}
      <div className={`app-badge-row ${layout === 'column' ? 'app-badge-row--col' : ''}`}>
        <a
          href={badgeHref('ios')}
          className="app-badge-link"
          target={iosLive ? '_blank' : undefined}
          rel={iosLive ? 'noopener noreferrer' : undefined}
          aria-label={iosLive ? 'Download on the App Store' : 'Join app waitlist — iOS coming soon'}
        >
          <AppStoreBadgeSvg />
        </a>
        <a
          href={badgeHref('android')}
          className="app-badge-link"
          target={androidLive ? '_blank' : undefined}
          rel={androidLive ? 'noopener noreferrer' : undefined}
          aria-label={
            androidLive ? 'Get it on Google Play' : 'Join app waitlist — Android coming soon'
          }
        >
          <GooglePlayBadgeSvg />
        </a>
      </div>
    </div>
  );
}

function AppStoreBadgeSvg() {
  return (
    <svg viewBox="0 0 120 40" className="app-badge-svg" aria-hidden>
      <rect width="120" height="40" rx="6" fill="#000" />
      <path
        d="M24.8 20.3c-.03-3.2 2.6-4.7 2.7-4.8-1.5-2.2-3.8-2.5-4.6-2.5-2-.2-3.9 1.2-4.9 1.2-1 0-2.6-1.1-4.3-1.1-2.2 0-4.2 1.3-5.3 3.3-2.3 3.9-.6 9.7 1.6 12.9 1.1 1.6 2.4 3.4 4.1 3.3 1.6-.1 2.2-1 4.1-1s2.5 1 4.2 1c1.7 0 2.8-1.6 3.9-3.2 1.2-1.8 1.7-3.5 1.7-3.6-.1 0-3.3-1.3-3.3-5zM21.2 11.5c.9-1.1 1.5-2.6 1.3-4.1-1.3.1-2.8.8-3.7 1.9-.8.9-1.5 2.4-1.3 3.8 1.4.1 2.9-.7 3.7-1.6z"
        fill="#fff"
      />
      <text x="44" y="14" fill="#fff" fontSize="7" fontFamily="system-ui,sans-serif">
        Download on the
      </text>
      <text
        x="44"
        y="28"
        fill="#fff"
        fontSize="13"
        fontWeight="600"
        fontFamily="system-ui,sans-serif"
      >
        App Store
      </text>
    </svg>
  );
}

function GooglePlayBadgeSvg() {
  return (
    <svg viewBox="0 0 135 40" className="app-badge-svg app-badge-svg--play" aria-hidden>
      <rect width="135" height="40" rx="6" fill="#000" />
      <path d="M8.5 8.2l12.8 7.4-12.8 7.4V8.2z" fill="#32BBFF" />
      <path d="M21.3 15.6l4.2 2.4-14.5 8.4 10.3-10.8z" fill="#F9AB00" />
      <path d="M21.3 24.4l-10.3 10.8 14.5-8.4-4.2-2.4z" fill="#F14336" />
      <path d="M25.5 20l-4.2-2.4 10.3-10.8-6.1 13.2z" fill="#4CAF50" />
      <text x="44" y="14" fill="#fff" fontSize="7" fontFamily="system-ui,sans-serif">
        GET IT ON
      </text>
      <text
        x="44"
        y="28"
        fill="#fff"
        fontSize="13"
        fontWeight="600"
        fontFamily="system-ui,sans-serif"
      >
        Google Play
      </text>
    </svg>
  );
}

export function AppStoreBadgesFooter() {
  return (
    <div className="mt-5">
      <p className="footer-social-label">Get the app</p>
      <AppStoreBadges showComingSoonHint={false} />
      <Link
        to="/contact?app=waitlist"
        className="text-primary-600 mt-2 inline-block text-[11px] font-semibold hover:underline"
      >
        Join the waitlist →
      </Link>
    </div>
  );
}
